import { Dispatch } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { PrivateApiCall } from "../../config/interceptors";

// Action Types
export const ANNOUNCEMENTS_ACTIONS = {
  GET_ANNOUNCEMENTS_REQUEST: "GET_ANNOUNCEMENTS_REQUEST",
  GET_ANNOUNCEMENTS_SUCCESS: "GET_ANNOUNCEMENTS_SUCCESS",
  GET_ANNOUNCEMENTS_FAILURE: "GET_ANNOUNCEMENTS_FAILURE",
  CREATE_ANNOUNCEMENT_REQUEST: "CREATE_ANNOUNCEMENT_REQUEST",
  CREATE_ANNOUNCEMENT_SUCCESS: "CREATE_ANNOUNCEMENT_SUCCESS",
  CREATE_ANNOUNCEMENT_FAILURE: "CREATE_ANNOUNCEMENT_FAILURE",
  UPDATE_ANNOUNCEMENT_REQUEST: "UPDATE_ANNOUNCEMENT_REQUEST",
  UPDATE_ANNOUNCEMENT_SUCCESS: "UPDATE_ANNOUNCEMENT_SUCCESS",
  UPDATE_ANNOUNCEMENT_FAILURE: "UPDATE_ANNOUNCEMENT_FAILURE",
  DELETE_ANNOUNCEMENT_REQUEST: "DELETE_ANNOUNCEMENT_REQUEST",
  DELETE_ANNOUNCEMENT_SUCCESS: "DELETE_ANNOUNCEMENT_SUCCESS",
  DELETE_ANNOUNCEMENT_FAILURE: "DELETE_ANNOUNCEMENT_FAILURE",
  TOGGLE_ANNOUNCEMENT_REQUEST: "TOGGLE_ANNOUNCEMENT_REQUEST",
  TOGGLE_ANNOUNCEMENT_SUCCESS: "TOGGLE_ANNOUNCEMENT_SUCCESS",
  TOGGLE_ANNOUNCEMENT_FAILURE: "TOGGLE_ANNOUNCEMENT_FAILURE",
  SET_SERVER_TIMEZONE: "ANNOUNCEMENTS/SET_SERVER_TIMEZONE",
};

// Interface for PlatformNotification (UPF Announcement)
export interface PlatformNotification {
  uuid: string;
  textEn: string;
  textFr: string;
  type: 'error' | 'info' | 'warning' | 'success';
  titleEn?: string | null;
  titleFr?: string | null;
  isScheduled: boolean;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  isActive: boolean;
}

// Get all UPF announcements
export const getAnnouncements = () => {
  return async (dispatch: ThunkDispatch<any, any, any>) => {
    dispatch({ type: ANNOUNCEMENTS_ACTIONS.GET_ANNOUNCEMENTS_REQUEST });
    try {
      const response = await PrivateApiCall.get("/admin/platform-notifications");
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.GET_ANNOUNCEMENTS_SUCCESS,
        payload: response.data.notifications,
      });
      if ((response as any).data?.serverTimezone) {
        dispatch({ type: ANNOUNCEMENTS_ACTIONS.SET_SERVER_TIMEZONE, payload: (response as any).data.serverTimezone });
      }
    } catch (error: any) {
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.GET_ANNOUNCEMENTS_FAILURE,
        payload: error.response?.data?.message || "Failed to fetch announcements",
      });
    }
  };
};

// Create new UPF announcement
export const createAnnouncement = (data: Partial<PlatformNotification>) => {
  return async (dispatch: ThunkDispatch<any, any, any>) => {
    dispatch({ type: ANNOUNCEMENTS_ACTIONS.CREATE_ANNOUNCEMENT_REQUEST });
    try {
      const announcementData = {
        ...data,
        type: data.type || 'warning', // Use provided type or default to warning
        isActive: false, // Always create as inactive
      };
      
      const response = await PrivateApiCall.post("/admin/platform-notifications", announcementData);
      
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.CREATE_ANNOUNCEMENT_SUCCESS,
        payload: response.data.notifications,
      });

      // Return created list so caller can publish immediately if needed
      return response.data.notifications;
    } catch (error: any) {
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.CREATE_ANNOUNCEMENT_FAILURE,
        payload: error.response?.data?.message || "Failed to create announcement",
      });
    }
  };
};

// Update existing UPF announcement
export const updateAnnouncement = (uuid: string, data: Partial<PlatformNotification>) => {
  return async (dispatch: ThunkDispatch<any, any, any>) => {
    dispatch({ type: ANNOUNCEMENTS_ACTIONS.UPDATE_ANNOUNCEMENT_REQUEST });
    try {
      const announcementData = {
        ...data,
        type: data.type || 'warning', // Use provided type or default to warning
        isActive: false, // Always update as inactive
      };
      
      const response = await PrivateApiCall.put(`/admin/platform-notifications/${uuid}`, announcementData);
      
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.UPDATE_ANNOUNCEMENT_SUCCESS,
        payload: response.data.notifications,
      });

      // Refresh the announcements list
      dispatch(getAnnouncements());
    } catch (error: any) {
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.UPDATE_ANNOUNCEMENT_FAILURE,
        payload: error.response?.data?.message || "Failed to update announcement",
      });
    }
  };
};

// Delete UPF announcement
export const deleteAnnouncement = (uuid: string) => {
  return async (dispatch: ThunkDispatch<any, any, any>) => {
    dispatch({ type: ANNOUNCEMENTS_ACTIONS.DELETE_ANNOUNCEMENT_REQUEST });
    try {
             await PrivateApiCall.delete(`/admin/platform-notifications/${uuid}`);
      
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.DELETE_ANNOUNCEMENT_SUCCESS,
      });

      // Refresh the announcements list
      dispatch(getAnnouncements());
    } catch (error: any) {
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.DELETE_ANNOUNCEMENT_FAILURE,
        payload: error.response?.data?.message || "Failed to delete announcement",
      });
    }
  };
};

// Toggle UPF announcement (enable/disable)
export const toggleAnnouncement = (uuid: string, isActive: boolean) => {
  return async (dispatch: ThunkDispatch<any, any, any>) => {
    dispatch({ type: ANNOUNCEMENTS_ACTIONS.TOGGLE_ANNOUNCEMENT_REQUEST });
    try {
      // If activating, use the single-active endpoint that will deactivate others
      // If deactivating, use the regular disable endpoint
      const endpoint = isActive 
        ? `/admin/platform-notifications/${uuid}/activate-single`
        : `/admin/platform-notifications/${uuid}/disable`;
        
      const response = await PrivateApiCall.put(endpoint, {});
      
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.TOGGLE_ANNOUNCEMENT_SUCCESS,
        payload: response.data.notifications,
      });

      // Refresh the announcements list
      dispatch(getAnnouncements());
    } catch (error: any) {
      dispatch({
        type: ANNOUNCEMENTS_ACTIONS.TOGGLE_ANNOUNCEMENT_FAILURE,
        payload: error.response?.data?.message || "Failed to toggle announcement",
      });
    }
  };
};

// Export all actions
const actions = {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  toggleAnnouncement,
};

export default actions;
